import { Checkbox, Descriptions, Empty, Image, List, Space, Tabs, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { IssuerAsset, IssuerAssetListItem, IssuerAssetSection, sectionTitlesAt } from '@mygate/common';
import { issuerStatusColor, mirrorWordDiffers } from '@/utils/issuerStatus';

const { Text, Paragraph } = Typography;

/**
 * Issuer content arrives as HTML fragments we do not control. The admin preview renders it as plain
 * text (DOMParser never executes scripts and we only read `textContent`), which is enough to judge
 * the content; the app gets a proper sanitizing renderer in the mobile phase.
 */
export function htmlToText(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  } catch {
    return html.replace(/<[^>]+>/g, '').trim();
  }
}

function formatBytes(bytes: number): string {
  if (!bytes) return '';
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function SectionView({ section, hidden }: { section: IssuerAssetSection; hidden: boolean }) {
  return (
    <div style={{ marginBottom: 12, opacity: hidden ? 0.4 : 1 }}>
      <Text strong>{section.title}</Text>
      {hidden && (
        <Tag style={{ marginLeft: 8 }} color="default">
          hidden in app
        </Tag>
      )}
      {section.text && (
        <Paragraph style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>{htmlToText(section.text)}</Paragraph>
      )}
      {section.list && (
        <ul style={{ marginBottom: 4, paddingLeft: 20 }}>
          {section.list.map((item, i) => (
            <li key={i}>{htmlToText(item)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface IssuerAssetPreviewProps {
  /** A list row carries the served state; a bare mirror record (the content card) does not. */
  issuerAsset: IssuerAsset & Partial<Pick<IssuerAssetListItem, 'servedStatus'>>;
  /**
   * Positions hidden in the app.
   *
   * Positions, not titles: the issuer publishes the same blocks in every locale with translated
   * titles, so a title hides one language and leaves the other three showing.
   */
  hiddenIndexes?: number[];
  /** When provided, sections get a "show in app" toggle. */
  onToggleHidden?: (index: number, hidden: boolean) => void;
  /** When provided, gallery images become selectable as the hero image. */
  selectedHeroUrl?: string;
  onSelectHero?: (url: string) => void;
}

/** What the app will show for this issuer record: facts, gallery, localized sections, documents. */
export function IssuerAssetPreview({
  issuerAsset,
  hiddenIndexes = [],
  onToggleHidden,
  selectedHeroUrl,
  onSelectHero,
}: IssuerAssetPreviewProps) {
  const locales = issuerAsset.locales.length > 0 ? issuerAsset.locales : Object.keys(issuerAsset.sections);
  const hiddenSet = new Set(hiddenIndexes);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions size="small" column={3} bordered>
        <Descriptions.Item label="Status">
          {/* The state customers are served when the row has it, as the list beside it shows (MYG-902). */}
          {issuerAsset.servedStatus !== undefined ? (
            <>
              <Tag color={issuerStatusColor(issuerAsset.servedStatus)}>{issuerAsset.servedStatus}</Tag>
              {mirrorWordDiffers(issuerAsset.status, issuerAsset.servedStatus) && (
                <Tag>mirror: {issuerAsset.status}</Tag>
              )}
            </>
          ) : (
            <Tag color={issuerStatusColor(issuerAsset.status)}>{issuerAsset.status}</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Issuer category">{issuerAsset.category ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Token symbol">
          {issuerAsset.tokenSymbol ?? <Text type="warning">not set by issuer</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Token price">
          {issuerAsset.tokenPriceEur !== undefined ? `${issuerAsset.tokenPriceEur} EUR` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Min investment">
          {issuerAsset.minInvestment !== undefined ? `${issuerAsset.minInvestment} EUR` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Window">
          {issuerAsset.openedAt ? issuerAsset.openedAt.slice(0, 10) : '—'} →{' '}
          {issuerAsset.closeAt ? issuerAsset.closeAt.slice(0, 10) : '—'}
          {issuerAsset.closedAt && <Text type="secondary"> (closed {issuerAsset.closedAt.slice(0, 10)})</Text>}
        </Descriptions.Item>
        {issuerAsset.supply && (
          <Descriptions.Item label="Supply" span={3}>
            {issuerAsset.supply.remainingTokens ?? '?'} of {issuerAsset.supply.totalTokens ?? '?'} tokens left
            <Text type="secondary" style={{ marginLeft: 8 }}>
              (as of {issuerAsset.supply.asOf.slice(0, 16).replace('T', ' ')})
            </Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      <div>
        <Text strong>Gallery ({issuerAsset.media.length})</Text>
        {issuerAsset.media.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No images from the issuer" />
        ) : (
          <Space wrap style={{ marginTop: 8 }}>
            {issuerAsset.media.map(item => {
              const selected = selectedHeroUrl === item.url;
              return (
                <div
                  key={item.url}
                  onClick={onSelectHero ? () => onSelectHero(item.url) : undefined}
                  style={{
                    position: 'relative',
                    cursor: onSelectHero ? 'pointer' : 'default',
                    border: selected ? '3px solid #722ed1' : '3px solid transparent',
                    borderRadius: 6,
                  }}
                  title={onSelectHero ? 'Use as hero image' : undefined}
                >
                  <Image
                    src={item.url}
                    width={96}
                    height={72}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    preview={!onSelectHero}
                  />
                  {selected && (
                    <CheckCircleFilled
                      style={{ position: 'absolute', top: 4, right: 4, color: '#722ed1', fontSize: 18 }}
                    />
                  )}
                </div>
              );
            })}
          </Space>
        )}
      </div>

      <div>
        <Text strong>Content</Text>
        {locales.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No published sections" />
        ) : (
          <Tabs
            size="small"
            items={locales.map(locale => ({
              key: locale,
              label: locale.toUpperCase(),
              children: (
                <div>
                  {(issuerAsset.sections[locale] ?? []).map((section, index) => (
                    <div key={`${locale}-${index}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      {onToggleHidden && (
                        <Tooltip
                          title={`Applies to every language: ${Object.entries(
                            sectionTitlesAt(issuerAsset.sections, index),
                          )
                            .map(([loc, title]) => `${loc.toUpperCase()} ${title}`)
                            .join(' · ')}`}
                        >
                          <Checkbox
                            checked={!hiddenSet.has(index)}
                            onChange={e => onToggleHidden(index, !e.target.checked)}
                            style={{ marginTop: 2 }}
                          />
                        </Tooltip>
                      )}
                      <div style={{ flex: 1 }}>
                        <SectionView section={section} hidden={hiddenSet.has(index)} />
                      </div>
                    </div>
                  ))}
                </div>
              ),
            }))}
          />
        )}
      </div>

      <div>
        <Text strong>Documents ({issuerAsset.documents.length})</Text>
        <List
          size="small"
          dataSource={issuerAsset.documents}
          locale={{ emptyText: 'No documents from the issuer' }}
          renderItem={doc => (
            <List.Item>
              <Space wrap>
                <Text>{Object.values(doc.title)[0] ?? doc.externalId}</Text>
                {Object.entries(doc.files).map(([locale, file]) => (
                  <a key={locale} href={file.fileUrl} target="_blank" rel="noreferrer">
                    {locale.toUpperCase()} · {file.fileName}{' '}
                    {formatBytes(file.fileSize) && `(${formatBytes(file.fileSize)})`}
                  </a>
                ))}
              </Space>
            </List.Item>
          )}
        />
      </div>
    </Space>
  );
}
